import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Calendar } from "@bcc/components/ui/calendar";
import { Input } from "@bcc/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@bcc/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@bcc/components/ui/select";
import type { InvoiceFilters, InvoiceStatus } from "@bcc/lib/types/invoice";
import { CalendarIcon, Search, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  setFilterBarContainerTestId,
  setFilterBarDateFilterButtonTestId,
  setFilterBarSearchButtonTestId,
  setFilterBarSearchInputTestId,
  setFilterBarStatusButtonTestId,
} from "./filter-bar.utils";

interface FilterBarProps {
  filters: InvoiceFilters;
  onFiltersChange: (filters: InvoiceFilters) => void;
  onSearch: (query: string) => void;
  testId?: string;
}

export function FilterBar({
  filters,
  onFiltersChange,
  onSearch,
  testId,
}: FilterBarProps) {
  const [searchQuery, setSearchQuery] = useState(filters.query || "");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(
    filters.dateFrom ? new Date(filters.dateFrom) : undefined,
  );
  const [dateTo, setDateTo] = useState<Date | undefined>(
    filters.dateTo ? new Date(filters.dateTo) : undefined,
  );
  const { t } = useTranslation();

  const handleStatusToggle = (status: InvoiceStatus) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((s) => s !== status)
      : [...currentStatuses, status];
    onFiltersChange({
      ...filters,
      status: newStatuses.length > 0 ? newStatuses : undefined,
    });
  };

  const handleDateFilterApply = () => {
    onFiltersChange({
      ...filters,
      dateFrom: dateFrom?.toISOString(),
      dateTo: dateTo?.toISOString(),
    });
    setShowDateFilter(false);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setDateFrom(undefined);
    setDateTo(undefined);
    onFiltersChange({});
    onSearch("");
  };

  const activeFilterCount =
    (filters.status?.length || 0) +
    (filters.dateFrom || filters.dateTo ? 1 : 0);

  return (
    <div
      data-testid={testId ?? setFilterBarContainerTestId()}
      className="space-y-4"
    >
      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid={setFilterBarSearchInputTestId()}
            placeholder={t("finance.filterBar.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSearch(searchQuery);
              }
            }}
            className="pl-9"
          />
        </div>
        <Button
          data-testid={setFilterBarSearchButtonTestId()}
          onClick={() => onSearch(searchQuery)}
        >
          {t("finance.filterBar.search")}
        </Button>
        <Popover open={showDateFilter} onOpenChange={setShowDateFilter}>
          <PopoverTrigger asChild>
            <Button
              data-testid={setFilterBarDateFilterButtonTestId()}
              variant="outline"
              className="gap-2"
            >
              <CalendarIcon className="h-4 w-4" />
              {t("finance.filterBar.dateFilter")}
              {(filters.dateFrom || filters.dateTo) && (
                <Badge variant="secondary" className="ml-1">
                  1
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4" align="end">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t("finance.filterBar.dateType")}
                </label>
                <Select
                  value={filters.dateType || "invoice"}
                  onValueChange={(value) =>
                    onFiltersChange({
                      ...filters,
                      dateType: value as InvoiceFilters["dateType"],
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">
                      {t("finance.filterBar.invoiceDate")}
                    </SelectItem>
                    <SelectItem value="pay">
                      {t("finance.filterBar.paymentDate")}
                    </SelectItem>
                    <SelectItem value="order">
                      {t("finance.filterBar.orderDate")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t("finance.filterBar.fromDate")}
                </label>
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={setDateFrom}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t("finance.filterBar.toDate")}
                </label>
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={setDateTo}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleDateFilterApply} className="flex-1">
                  {t("finance.filterBar.apply")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDateFrom(undefined);
                    setDateTo(undefined);
                    onFiltersChange({
                      ...filters,
                      dateFrom: undefined,
                      dateTo: undefined,
                    });
                  }}
                >
                  {t("finance.filterBar.clear")}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            onClick={handleClearFilters}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            {t("finance.filterBar.clearAll")}
          </Button>
        )}
      </div>

      {/* Filter Chips */}
      <div className="flex flex-wrap gap-2">
        <Button
          data-testid={setFilterBarStatusButtonTestId("paid")}
          variant={filters.status?.includes("Paid") ? "default" : "outline"}
          size="sm"
          onClick={() => handleStatusToggle("Paid")}
          className="gap-1"
        >
          <div className="h-2 w-2 rounded-full bg-green-500" />
          {t("finance.filterBar.status.paid")}
        </Button>
        <Button
          data-testid={setFilterBarStatusButtonTestId("unpaid")}
          variant={filters.status?.includes("Unpaid") ? "default" : "outline"}
          size="sm"
          onClick={() => handleStatusToggle("Unpaid")}
          className="gap-1"
        >
          <div className="h-2 w-2 rounded-full bg-yellow-500" />
          {t("finance.filterBar.status.unpaid")}
        </Button>
        <Button
          data-testid={setFilterBarStatusButtonTestId("overdue")}
          variant={filters.status?.includes("Overdue") ? "default" : "outline"}
          size="sm"
          onClick={() => handleStatusToggle("Overdue")}
          className="gap-1"
        >
          <div className="h-2 w-2 rounded-full bg-red-500" />
          {t("finance.filterBar.status.overdue")}
        </Button>
      </div>
    </div>
  );
}
